import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { supabase } from '../supabaseClient';

const C = {
  bg: '#0a0a0a', surface: '#131313', surface2: '#1c1c1c', surface3: '#232323',
  border: '#2a2a2a', borderStrong: '#3a3a3a', muted: '#8a8a8a', textDim: '#b8b8b8',
  accent: '#9D1515', accentDim: 'rgba(157,21,21,0.15)',
  gold: '#d8a64a', bronze: '#b87333', live: '#6bd6a3',
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Manrope:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');
@keyframes sbpulse{0%,100%{opacity:1}50%{opacity:.4}}
.sb2{font-family:'Manrope',system-ui,sans-serif;-webkit-font-smoothing:antialiased;background:#0a0a0a;min-height:100vh;color:#f5f5f5;}
.sb2 *{box-sizing:border-box;}
.sb2-mc{background:#1c1c1c;border:1px solid #2a2a2a;cursor:pointer;transition:border-color .15s,background .15s;display:block;width:100%;text-align:left;color:inherit;padding:0;font-family:inherit;}
.sb2-mc:hover{border-color:#9D1515;background:#232323;}
.sb2-mc.is-live{border-color:#6bd6a3;background:rgba(107,214,163,.04);}
.sb2-pool{background:#131313;border:1px solid #2a2a2a;}
.sb2-round{background:#131313;border:1px solid #2a2a2a;}
.sb2-std{width:100%;border-collapse:collapse;}
.sb2-std th{text-align:left;font-size:9px;letter-spacing:.22em;text-transform:uppercase;color:#8a8a8a;font-weight:700;padding:12px 14px 10px;border-bottom:1px solid #2a2a2a;background:#1c1c1c;font-family:'JetBrains Mono',monospace;}
.sb2-std th.r{text-align:right;}
.sb2-std td{padding:11px 14px;border-bottom:1px solid #2a2a2a;font-size:13px;color:#b8b8b8;}
.sb2-std td.r{text-align:right;font-family:'JetBrains Mono',monospace;font-size:12px;}
.sb2-std tr:last-child td{border-bottom:none;}
.sb2-std tr.adv td:first-child{border-left:2px solid #9D1515;}
.sb2-std tr.adv .sb2-seed{color:#9D1515;font-weight:600;}
.sb2-std tr.elim td{color:#555;}
.sb2-std tr.elim .sb2-tn{color:#888;text-decoration:line-through;text-decoration-color:#555;}
.sb2-panel{position:fixed;top:0;right:0;bottom:0;width:min(520px,100%);background:#131313;border-left:1px solid #3a3a3a;z-index:95;transform:translateX(100%);transition:transform .25s cubic-bezier(.22,.61,.36,1);display:flex;flex-direction:column;overflow-y:auto;}
.sb2-panel.open{transform:translateX(0);}
.sb2-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:90;opacity:0;pointer-events:none;transition:opacity .2s;}
.sb2-backdrop.open{opacity:1;pointer-events:auto;}
.sb2-pclose{background:transparent;border:1px solid #3a3a3a;color:white;width:34px;height:34px;display:grid;place-items:center;font-size:20px;cursor:pointer;line-height:1;font-family:sans-serif;}
.sb2-pclose:hover{border-color:white;}
.sb2-pill{display:inline-flex;align-items:center;gap:6px;font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:.14em;padding:4px 9px;border:1px solid #3a3a3a;color:#b8b8b8;}
.sb2-pill.live{color:#6bd6a3;border-color:rgba(107,214,163,.4);}
.sb2-pill.final{color:white;border-color:#9D1515;background:rgba(157,21,21,.15);}
.sb2-pill.upcoming{color:#8a8a8a;}
.sb2-dot{animation:sbpulse 1.6s ease-in-out infinite;display:inline-block;border-radius:50%;flex-shrink:0;}
.sb2-sh{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin:36px 0 16px;border-bottom:1px solid #2a2a2a;padding-bottom:12px;}
.sb2-h2{font-family:'Bebas Neue',sans-serif;font-size:30px;margin:0;letter-spacing:.04em;}
.sb2-h2 em{color:#9D1515;font-style:italic;font-weight:400;}
@media(max-width:900px){.sb2-rounds{grid-template-columns:1fr!important;}.sb2-finals{grid-template-columns:1fr!important;}.sb2-pools{grid-template-columns:1fr!important;}}
@media(max-width:1100px){.sb2-bracket{grid-template-columns:1fr!important;}}
@media(max-width:900px){.sb2-bracket{min-width:0!important;}.sb2-semis-col{justify-content:flex-start!important;gap:12px!important;}}
`;

const SETS_FOR_STAGE = { pool: 1, quarter: 1, semi: 3, bronze: 3, final: 1 };
const PLAYOFF_STAGES = ['quarter', 'semi', 'bronze', 'final'];

function setsToWin(stage) { return Math.ceil((SETS_FOR_STAGE[stage] || 1) / 2); }

function getWinner(match) {
  if (!match || match.status !== 'completed') return null;
  const sets = match.sets || [];
  if (!sets.length) return null;
  const toWin = setsToWin(match.stage);
  let t1 = 0, t2 = 0;
  for (const s of sets) {
    if ((s.t1 || 0) > (s.t2 || 0)) t1++; else if ((s.t2 || 0) > (s.t1 || 0)) t2++;
  }
  if (t1 >= toWin) return match.team1_name;
  if (t2 >= toWin) return match.team2_name;
  return null;
}

function computeStandings(matches) {
  const pools = {};
  // Seed every team from ALL pool matches so the standings board shows from the
  // start (0-0), then accumulate stats from completed games below.
  for (const m of matches) {
    if (m.stage !== 'pool') continue;
    const pool = m.pool || 'ALL';
    if (!pools[pool]) pools[pool] = {};
    for (const name of [m.team1_name, m.team2_name]) {
      if (name && name !== 'TBD' && !pools[pool][name])
        pools[pool][name] = { name, pts: 0, gp: 0, w: 0, l: 0, diff: 0 };
    }
  }
  for (const m of matches) {
    if (m.stage !== 'pool' || m.status !== 'completed') continue;
    const pool = m.pool || 'ALL';
    const s = (m.sets || [])[0];
    if (!s) continue;
    const t1 = pools[pool][m.team1_name], t2 = pools[pool][m.team2_name];
    if (!t1 || !t2) continue;
    t1.gp++; t2.gp++;
    t1.diff += (s.t1 || 0) - (s.t2 || 0);
    t2.diff += (s.t2 || 0) - (s.t1 || 0);
    if (s.t1 > s.t2) { t1.pts += 2; t1.w++; t2.l++; }
    else if (s.t2 > s.t1) { t2.pts += 2; t2.w++; t1.l++; }
    else { t1.pts++; t2.pts++; }
  }
  const result = {};
  for (const [pool, teams] of Object.entries(pools))
    result[pool] = Object.values(teams).sort((a, b) => b.pts - a.pts || b.w - a.w || b.diff - a.diff);
  return result;
}

/* ── MatchCard ─────────────────────────────────────────────── */
function MatchCard({ match, onOpen }) {
  const winner = getWinner(match);
  const sets = match.sets || [];
  const isLive = match.status === 'live';
  const isCompleted = match.status === 'completed';
  const isMulti = (SETS_FOR_STAGE[match.stage] || 1) > 1;
  const t1w = sets.filter(s => (s.t1 || 0) > (s.t2 || 0)).length;
  const t2w = sets.filter(s => (s.t2 || 0) > (s.t1 || 0)).length;
  const s0 = sets[0];
  const gameNum = String(match.match_number).padStart(2, '0');
  const stageTag = { quarter: 'QF', semi: 'SF', bronze: 'BRONZE', final: 'CHAMPIONSHIP' }[match.stage]
    || (match.pool ? `POOL ${match.pool}` : 'POOL');

  const TeamRow = ({ name, isWin, score, last }) => (
    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: last ? 'none' : `1px solid ${C.border}`, fontSize: 13 }}>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: isWin ? C.accent : C.muted, letterSpacing: '0.04em' }}>
        {isWin ? '▸' : '·'}
      </span>
      <span style={{ fontWeight: 600, color: isWin ? 'white' : (!isCompleted && !isLive) ? '#555' : C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: (!name || name === 'TBD') ? 'italic' : 'normal' }}>
        {name || 'TBD'}
      </span>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: isWin ? 'white' : C.muted }}>
        {(isCompleted || isLive) ? (score ?? '-') : '-'}
      </span>
    </div>
  );

  return (
    <button className={`sb2-mc${isLive ? ' is-live' : ''}`} onClick={() => onOpen && onOpen(match)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px 6px', borderBottom: `1px solid ${C.border}`, background: 'rgba(0,0,0,.2)' }}>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.muted, letterSpacing: '0.14em' }}>
          GAME {gameNum} · {stageTag}
        </span>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 6, color: isLive ? C.live : C.textDim }}>
          {isLive && <span className="sb2-dot" style={{ width: 6, height: 6, background: C.live }} />}
          {isLive ? 'LIVE' : match.court ? match.court.toUpperCase() : ''}
        </span>
      </div>
      <TeamRow name={match.team1_name} isWin={winner === match.team1_name} score={isMulti ? t1w : s0?.t1} last={false} />
      <TeamRow name={match.team2_name} isWin={winner === match.team2_name} score={isMulti ? t2w : s0?.t2} last={true} />
      <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.border}`, fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.muted, letterSpacing: '0.06em', display: 'flex', justifyContent: 'space-between' }}>
        <span>{isMulti ? 'BEST OF 3 · 25 / 25 / 15' : 'FIRST TO 25'}</span>
        <span>{isCompleted ? 'FINAL' : isLive ? '' : match.scheduled_time ? new Date(match.scheduled_time).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true }) : 'UPCOMING'}</span>
      </div>
    </button>
  );
}

/* ── StandingsTable ────────────────────────────────────────── */
function StandingsTable({ teams, advanceCount = 0 }) {
  if (!teams.length) return null;
  // Only show ADVANCE / ELIMINATED once pool play has actually started (a team has
  // played a game) — before that everyone is 0-0 and the order is meaningless.
  const started = teams.some(t => t.gp > 0);
  return (
    <table className="sb2-std">
      <thead>
        <tr>
          <th>TEAM</th>
          {['PTS', 'W', 'L', '+/−'].map(h => <th key={h} className="r">{h}</th>)}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {teams.map((t, i) => {
          const adv = started && advanceCount > 0 && i < advanceCount;
          const elim = started && advanceCount > 0 && i >= advanceCount;
          return (
            <tr key={t.name} className={adv ? 'adv' : elim ? 'elim' : ''}>
              <td>
                <span className="sb2-seed" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: adv ? C.accent : C.muted, display: 'inline-block', width: 24, marginRight: 8 }}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="sb2-tn" style={{ fontWeight: 600, color: 'white' }}>{t.name}</span>
              </td>
              <td className="r" style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: C.gold }}>{t.pts}</td>
              <td className="r" style={{ color: t.w > 0 ? C.live : C.muted }}>{t.w}</td>
              <td className="r" style={{ color: t.l > 0 ? C.accent : C.muted }}>{t.l}</td>
              <td className="r" style={{ color: t.diff > 0 ? C.live : t.diff < 0 ? C.accent : C.muted }}>{t.diff > 0 ? '+' : ''}{t.diff}</td>
              <td style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.08em' }}>
                {adv && <span style={{ color: C.accent }}>ADVANCE →</span>}
                {elim && <span style={{ color: '#555' }}>ELIMINATED</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ── DetailPanel ───────────────────────────────────────────── */
function DetailPanel({ match, onClose }) {
  const open = !!match;
  const winner = match ? getWinner(match) : null;
  const sets = match?.sets || [];
  const isLive = match?.status === 'live';
  const isCompleted = match?.status === 'completed';
  const isMulti = (SETS_FOR_STAGE[match?.stage] || 1) > 1;
  const t1w = sets.filter(s => (s.t1 || 0) > (s.t2 || 0)).length;
  const t2w = sets.filter(s => (s.t2 || 0) > (s.t1 || 0)).length;
  const maxSets = SETS_FOR_STAGE[match?.stage] || 1;
  const gameNum = match ? String(match.match_number).padStart(2, '0') : '';
  const stageLabel = match ? ({ quarter: 'QUARTER FINAL', semi: 'SEMI FINAL', bronze: 'BRONZE', final: 'CHAMPIONSHIP', pool: match.pool ? `POOL ${match.pool}` : 'POOL PLAY' }[match.stage] || 'MATCH') : '';
  const s0 = sets[0];

  const pointDiff = (() => {
    if (!winner || !sets.length) return 0;
    return sets.reduce((acc, s) => {
      const t1 = s.t1 || 0, t2 = s.t2 || 0;
      return acc + (winner === match.team1_name ? t1 - t2 : t2 - t1);
    }, 0);
  })();

  return (
    <>
      <div className={`sb2-backdrop${open ? ' open' : ''}`} onClick={onClose} />
      <div className={`sb2-panel${open ? ' open' : ''}`}>
        {match && (
          <>
            <div style={{ padding: '22px 24px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: C.surface, zIndex: 2 }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.accent, letterSpacing: '0.18em', marginBottom: 6 }}>
                  GAME {gameNum} · {stageLabel}
                </div>
                <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 26, margin: '0 0 4px', letterSpacing: '0.04em', lineHeight: 1.1 }}>
                  {match.team1_name || 'TBD'}{' '}
                  <em style={{ color: C.accent, fontStyle: 'italic', fontWeight: 400 }}>vs</em>{' '}
                  {match.team2_name || 'TBD'}
                </h2>
                {match.court && (
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.textDim, letterSpacing: '0.08em' }}>
                    {match.court.toUpperCase()}
                  </div>
                )}
              </div>
              <button className="sb2-pclose" onClick={onClose}>×</button>
            </div>

            <div style={{ padding: '22px 24px 40px' }}>
              <div style={{ marginBottom: 20 }}>
                <span className={`sb2-pill${isLive ? ' live' : isCompleted ? ' final' : ' upcoming'}`}>
                  {isLive && <span className="sb2-dot" style={{ width: 6, height: 6, background: C.live }} />}
                  {isLive ? 'LIVE NOW' : isCompleted ? 'FINAL' : 'UPCOMING'}
                </span>
              </div>

              {(isCompleted || isLive) && sets.length > 0 ? (
                <div style={{ border: `1px solid ${C.border}`, marginBottom: 22 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: `28px 1fr ${Array.from({ length: maxSets }, () => '52px').join(' ')} 60px`, background: C.surface2, padding: '10px 16px', fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.muted }}>
                    <span />
                    <span>TEAM</span>
                    {Array.from({ length: maxSets }, (_, i) => <span key={i} style={{ textAlign: 'center' }}>S{i + 1}</span>)}
                    <span style={{ textAlign: 'center' }}>{isMulti ? 'SETS' : 'PTS'}</span>
                  </div>
                  {[match.team1_name, match.team2_name].map((name, si) => {
                    const isW = winner === name;
                    const sw = si === 0 ? t1w : t2w;
                    const totalPts = si === 0 ? (s0?.t1 ?? 0) : (s0?.t2 ?? 0);
                    return (
                      <div key={si} style={{ display: 'grid', gridTemplateColumns: `28px 1fr ${Array.from({ length: maxSets }, () => '52px').join(' ')} 60px`, alignItems: 'center', padding: '14px 16px', borderTop: `1px solid ${C.border}` }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: isW ? C.accent : C.muted }}>{isW ? '▸' : '·'}</span>
                        <span style={{ fontWeight: 700, fontSize: 14, color: isW ? 'white' : C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'TBD'}</span>
                        {Array.from({ length: maxSets }, (_, i) => {
                          const s = sets[i];
                          const sc = s ? (si === 0 ? s.t1 : s.t2) : null;
                          const op = s ? (si === 0 ? s.t2 : s.t1) : null;
                          const win = sc != null && op != null && sc > op;
                          return (
                            <span key={i} style={{ textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 14, color: sc == null ? '#333' : win ? 'white' : C.muted, fontWeight: win ? 600 : 400 }}>
                              {sc ?? '·'}
                            </span>
                          );
                        })}
                        <span style={{ textAlign: 'center', fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color: isW ? 'white' : C.textDim, letterSpacing: '0.04em' }}>
                          {isMulti ? sw : totalPts}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 24px', color: C.muted, fontSize: 13, border: `1px dashed ${C.borderStrong}`, marginBottom: 22 }}>
                  <b style={{ color: 'white', display: 'block', fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: '0.06em', marginBottom: 8 }}>NOT YET PLAYED</b>
                  {match.scheduled_time && <span>Scheduled {new Date(match.scheduled_time).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>}
                </div>
              )}

              {isCompleted && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: C.border, border: `1px solid ${C.border}`, marginBottom: 22 }}>
                  {[
                    { lbl: 'WINNER', val: winner || '-' },
                    { lbl: 'POINT DIFF', val: winner ? `+${Math.abs(pointDiff)}` : '-' },
                    { lbl: 'FORMAT', val: isMulti ? 'BEST OF 3' : 'SINGLE SET', sub: isMulti ? '25 / 25 / 15' : 'FIRST TO 25' },
                    { lbl: 'COURT', val: match.court || '-' },
                  ].map(({ lbl, val, sub }) => (
                    <div key={lbl} style={{ background: C.bg, padding: '14px 16px' }}>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>{lbl}</div>
                      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: '0.04em' }}>{val}</div>
                      {sub && <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: C.muted, letterSpacing: '0.04em', marginTop: 4 }}>{sub}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/* ── PlayoffBracket ────────────────────────────────────────── */
function PlayoffBracket({ matches, onOpen }) {
  const quarters = matches.filter(m => m.stage === 'quarter').sort((a, b) => a.match_number - b.match_number);
  const semis    = matches.filter(m => m.stage === 'semi').sort((a, b) => a.match_number - b.match_number);
  const bronze   = matches.filter(m => m.stage === 'bronze');
  const finals   = matches.filter(m => m.stage === 'final');
  const hasQF    = quarters.length > 0;
  const champion = finals.length > 0 ? getWinner(finals[0]) : null;

  const containerRef = useRef(null);
  const cardRefs = useRef({});
  const [paths, setPaths] = useState([]);
  const mkRef = (id) => (el) => { cardRefs.current[id] = el; };

  const recalc = () => {
    const c = containerRef.current;
    if (!c) return;
    const cr = c.getBoundingClientRect();
    const newPaths = [];
    const get = (id) => {
      const el = cardRefs.current[id];
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { lx: r.left - cr.left, rx: r.right - cr.left, my: r.top + r.height / 2 - cr.top };
    };
    const addPair = (a, b, t) => {
      if (!a || !b || !t) return;
      const jx = (Math.max(a.rx, b.rx) + t.lx) / 2;
      newPaths.push(`M ${a.rx} ${a.my} H ${jx}`);
      newPaths.push(`M ${b.rx} ${b.my} H ${jx}`);
      newPaths.push(`M ${jx} ${Math.min(a.my, b.my)} V ${Math.max(a.my, b.my)}`);
      newPaths.push(`M ${jx} ${t.my} H ${t.lx}`);
    };
    const addSingle = (src, t) => {
      if (!src || !t) return;
      newPaths.push(`M ${src.rx} ${src.my} H ${t.lx}`);
    };
    if (hasQF) {
      if (quarters[0] && quarters[1] && semis[0]) addPair(get(quarters[0].id), get(quarters[1].id), get(semis[0].id));
      if (quarters[2] && quarters[3] && semis[1]) addPair(get(quarters[2].id), get(quarters[3].id), get(semis[1].id));
    }
    if (semis.length >= 2 && finals[0]) addPair(get(semis[0].id), get(semis[1].id), get(finals[0].id));
    else if (semis.length === 1 && finals[0]) addSingle(get(semis[0].id), get(finals[0].id));
    setPaths(newPaths);
  };

  useLayoutEffect(() => { recalc(); }, [matches]); // eslint-disable-line

  const ColHead = ({ label, sub }) => (
    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: '0.14em', color: C.muted, paddingBottom: 8, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
      {label}
      {sub && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.08em', color: C.textDim }}>{sub}</span>}
    </div>
  );

  const cols = hasQF ? 3 : (semis.length > 0 ? 2 : 1);

  return (
    <div ref={containerRef} style={{ position: 'relative', overflowX: 'auto', paddingBottom: 20 }}>
      <svg style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible', zIndex: 0 }} aria-hidden="true">
        {paths.map((d, i) => <path key={i} d={d} stroke={C.border} strokeWidth={1} fill="none" strokeLinecap="square" />)}
      </svg>

      {champion && (
        <div style={{ borderTop: `1px solid rgba(216,166,74,.18)`, borderBottom: `1px solid rgba(216,166,74,.18)`, padding: '20px 0', marginBottom: 28, position: 'relative', zIndex: 1 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.gold, letterSpacing: '0.28em', marginBottom: 8 }}>TOURNAMENT CHAMPIONS</div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 36, letterSpacing: '0.06em', color: 'white', lineHeight: 1 }}>{champion}</div>
        </div>
      )}

      <div className="sb2-bracket" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 28, minWidth: hasQF ? 780 : 480, position: 'relative', zIndex: 1 }}>
        {hasQF && (
          <div>
            <ColHead label="QUARTER FINALS" sub={`${quarters.length} MATCHES`} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {quarters.map((m, i) => (
                <React.Fragment key={m.id}>
                  <div ref={mkRef(m.id)}><MatchCard match={m} onOpen={onOpen} /></div>
                  {i === 1 && <div style={{ height: 16 }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {semis.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <ColHead label="SEMI FINALS" sub={`${semis.length} MATCHES`} />
            <div className="sb2-semis-col" style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, justifyContent: hasQF ? 'space-around' : 'flex-start' }}>
              {semis.map((m, i) => (
                <React.Fragment key={m.id}>
                  <div ref={mkRef(m.id)}><MatchCard match={m} onOpen={onOpen} /></div>
                  {i === 0 && semis.length > 1 && <div style={{ height: hasQF ? 56 : 8 }} />}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <ColHead label="CHAMPIONSHIP" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, justifyContent: 'center' }}>
            {bronze.map(m => (
              <React.Fragment key={m.id}>
                <div ref={mkRef(m.id)}><MatchCard match={m} onOpen={onOpen} /></div>
                <div style={{ height: 16 }} />
              </React.Fragment>
            ))}
            {finals.map(m => (
              <React.Fragment key={m.id}>
                <div ref={mkRef(m.id)}><MatchCard match={m} onOpen={onOpen} /></div>
                <div style={{ background: 'linear-gradient(180deg,rgba(216,166,74,.08),transparent)', border: `1px solid rgba(216,166,74,.4)`, padding: '18px 14px', marginTop: 14 }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, color: C.gold, letterSpacing: '0.14em', textAlign: 'center' }}>CHAMPION</div>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, textAlign: 'center', marginTop: 6, color: champion ? 'white' : '#555' }}>
                    {champion || 'TBD'}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── BlitzView ─────────────────────────────────────────────── */
function BlitzView({ matches, standings, onOpen }) {
  const poolTeams = standings['ALL'] || [];
  const poolMatches = matches.filter(m => m.stage === 'pool').sort((a, b) => a.match_number - b.match_number);
  const bronzeMatch = matches.find(m => m.stage === 'bronze');
  const finalMatch = matches.find(m => m.stage === 'final');

  const rounds = [];
  for (let i = 0; i < poolMatches.length; i += 2) rounds.push(poolMatches.slice(i, i + 2));

  const roundStatus = (rm) => {
    if (rm.every(m => m.status === 'completed')) return { live: false, label: 'DONE', color: C.textDim };
    if (rm.some(m => m.status === 'live')) return { live: true, label: 'LIVE', color: C.live };
    return { live: false, label: 'UPCOMING', color: C.muted };
  };

  return (
    <>
      <div className="sb2-sh">
        <h2 className="sb2-h2">POOL <em>PLAY</em></h2>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: C.muted, letterSpacing: '0.08em' }}>{poolMatches.length} GAMES</span>
      </div>

      {poolTeams.length > 0 && (
        <div className="sb2-pool" style={{ marginBottom: 24 }}>
          <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, margin: 0, letterSpacing: '0.06em' }}>STANDINGS</h3>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.textDim, letterSpacing: '0.06em', border: `1px solid ${C.borderStrong}`, padding: '4px 8px' }}>ALL TEAMS</span>
          </div>
          <StandingsTable teams={poolTeams} advanceCount={0} />
        </div>
      )}

      {rounds.length > 0 && (
        <div className="sb2-rounds" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 40 }}>
          {rounds.map((rm, ri) => {
            const st = roundStatus(rm);
            return (
              <div key={ri} className="sb2-round">
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, margin: 0, letterSpacing: '0.1em' }}>ROUND {ri + 1}</h4>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, letterSpacing: '0.1em', color: st.color, display: 'flex', alignItems: 'center', gap: 5 }}>
                    {st.live && <span className="sb2-dot" style={{ width: 5, height: 5, background: C.live }} />}
                    {st.label}
                  </span>
                </div>
                {rm.map((m, mi) => (
                  <div key={m.id} style={{ borderBottom: mi < rm.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <MatchCard match={m} onOpen={onOpen} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {(finalMatch || bronzeMatch) && (
        <>
          <div className="sb2-sh">
            <h2 className="sb2-h2">FINALS</h2>
          </div>
          <div className="sb2-finals" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            {finalMatch && (
              <div style={{ background: `linear-gradient(180deg,rgba(216,166,74,.06),${C.surface} 60%)`, border: `1px solid rgba(216,166,74,.45)`, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                  <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, margin: 0, letterSpacing: '0.04em' }}>
                    GOLD <em style={{ color: C.gold, fontStyle: 'normal', fontWeight: 400 }}>FINAL</em>
                  </h3>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.textDim, letterSpacing: '0.08em' }}>BEST OF 3</span>
                </div>
                <MatchCard match={finalMatch} onOpen={onOpen} />
              </div>
            )}
            {bronzeMatch && (
              <div style={{ background: `linear-gradient(180deg,rgba(184,115,51,.06),${C.surface} 60%)`, border: `1px solid rgba(184,115,51,.45)`, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                  <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, margin: 0, letterSpacing: '0.04em' }}>
                    BRONZE <em style={{ color: C.bronze, fontStyle: 'normal', fontWeight: 400 }}>FINAL</em>
                  </h3>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.textDim, letterSpacing: '0.08em' }}>BEST OF 3</span>
                </div>
                <MatchCard match={bronzeMatch} onOpen={onOpen} />
              </div>
            )}
          </div>
        </>
      )}

      {rounds.length === 0 && !finalMatch && !bronzeMatch && (
        <div style={{ textAlign: 'center', padding: '80px 0', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#333', letterSpacing: '0.14em' }}>
          POOL PLAY HAS NOT STARTED
        </div>
      )}
    </>
  );
}

/* ── CupView ───────────────────────────────────────────────── */
function CupView({ matches, standings, onOpen }) {
  const poolATeams = standings['A'] || [];
  const poolBTeams = standings['B'] || [];
  const poolAMatches = matches.filter(m => m.stage === 'pool' && m.pool === 'A').sort((a, b) => a.match_number - b.match_number);
  const poolBMatches = matches.filter(m => m.stage === 'pool' && m.pool === 'B').sort((a, b) => a.match_number - b.match_number);
  const playoffMatches = matches.filter(m => PLAYOFF_STAGES.includes(m.stage));

  const PoolPanel = ({ pool, teams, pMatches }) => {
    const court = pMatches.find(m => m.court)?.court;
    const poolDone = pMatches.length > 0 && pMatches.every(m => m.status === 'completed');
    return (
      <div className="sb2-pool">
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, margin: 0, letterSpacing: '0.06em' }}>POOL {pool}</h3>
          {court && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.textDim, letterSpacing: '0.06em', border: `1px solid ${C.borderStrong}`, padding: '4px 8px' }}>{court.toUpperCase()}</span>}
        </div>
        <StandingsTable teams={teams} advanceCount={poolDone ? 4 : 0} />
        {pMatches.length > 0 && (
          <div style={{ borderTop: `1px solid ${C.border}` }}>
            <div style={{ padding: '10px 14px', fontFamily: "'Bebas Neue',sans-serif", fontSize: 13, letterSpacing: '0.14em', color: C.muted, borderBottom: `1px solid ${C.border}` }}>MATCHES</div>
            {pMatches.map(m => <MatchCard key={m.id} match={m} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="sb2-sh">
        <h2 className="sb2-h2">POOL <em>PLAY</em></h2>
      </div>
      <div className="sb2-pools" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 40 }}>
        <PoolPanel pool="A" teams={poolATeams} pMatches={poolAMatches} />
        <PoolPanel pool="B" teams={poolBTeams} pMatches={poolBMatches} />
      </div>

      {playoffMatches.length > 0 && (
        <>
          <div className="sb2-sh">
            <h2 className="sb2-h2">PLAYOFFS</h2>
          </div>
          <PlayoffBracket matches={playoffMatches} onOpen={onOpen} />
        </>
      )}
    </>
  );
}

/* ── Scoreboard ────────────────────────────────────────────── */
export default function Scoreboard({ onBack }) {
  const tournId = window.location.pathname.split('/scoreboard/')[1]?.split('/')[0] ||
                  new URLSearchParams(window.location.search).get('t') || '';

  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelMatch, setPanelMatch] = useState(null);

  useEffect(() => {
    if (!tournId) { setLoading(false); return; }
    supabase.from('seasons').select('id, name').eq('id', tournId).maybeSingle()
      .then(({ data }) => setTournament(data));
    supabase.from('tournament_matches').select('*').eq('tournament_id', tournId)
      .order('match_number')
      .then(({ data }) => { setMatches(data || []); setLoading(false); });

    const ch = supabase.channel(`sb:${tournId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tournament_matches', filter: `tournament_id=eq.${tournId}` }, payload => {
        setMatches(prev => {
          if (payload.eventType === 'INSERT') return [...prev, payload.new].sort((a, b) => a.match_number - b.match_number);
          if (payload.eventType === 'UPDATE') return prev.map(m => m.id === payload.new.id ? payload.new : m);
          if (payload.eventType === 'DELETE') return prev.filter(m => m.id !== payload.old.id);
          return prev;
        });
      }).subscribe();

    return () => supabase.removeChannel(ch);
  }, [tournId]);

  const standings = useMemo(() => computeStandings(matches), [matches]);
  const isCup = useMemo(() => matches.some(m => m.pool === 'A' || m.pool === 'B'), [matches]);
  const liveCount = matches.filter(m => m.status === 'live').length;
  const completedCount = matches.filter(m => m.status === 'completed').length;

  if (!tournId) return (
    <div className="sb2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 40, textAlign: 'center' }}>
      <style>{CSS}</style>
      <div>
        <img src="/ovr-logo.png" alt="OVR" style={{ height: 64, width: 'auto', display: 'inline-block' }} />
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.muted, marginTop: 8, letterSpacing: '0.14em' }}>NO TOURNAMENT SPECIFIED</div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="sb2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <style>{CSS}</style>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.muted, letterSpacing: '0.14em' }}>LOADING…</div>
    </div>
  );

  return (
    <div className="sb2">
      <style>{CSS}</style>

      {/* Sticky nav */}
      <div style={{ position: 'sticky', top: 0, zIndex: 30, backdropFilter: 'blur(14px)', background: 'rgba(10,10,10,.85)', borderBottom: `1px solid ${C.border}`, padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: C.muted, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6, fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', transition: 'color .15s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = C.muted}
            >
              ← BACK
            </button>
          )}
          <img src="/ovr-logo.png" alt="OVR" style={{ height: 30, width: 'auto', display: 'block' }} />
        </div>
        {liveCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="sb2-dot" style={{ width: 7, height: 7, background: C.live }} />
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: C.live, letterSpacing: '0.18em' }}>{liveCount} LIVE</span>
          </div>
        )}
      </div>

      {/* Page header */}
      <div style={{ padding: '36px 28px 0' }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.accent, fontWeight: 700, marginBottom: 12 }}>
          {isCup ? 'OVR CUP' : 'BLITZ'} · LIVE SCOREBOARD
        </div>
        <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(40px, 6vw, 76px)', lineHeight: 0.95, margin: '0 0 14px', letterSpacing: '0.02em' }}>
          {tournament?.name || tournId.toUpperCase()}
        </h1>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: C.muted, marginBottom: 36, letterSpacing: '0.06em' }}>
          {completedCount} OF {matches.length} MATCHES COMPLETE
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '0 28px 100px' }}>
        {isCup
          ? <CupView matches={matches} standings={standings} onOpen={setPanelMatch} />
          : <BlitzView matches={matches} standings={standings} onOpen={setPanelMatch} />
        }
      </div>

      <DetailPanel match={panelMatch} onClose={() => setPanelMatch(null)} />
    </div>
  );
}
